import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Tour } from 'antd';
import type { TourProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';

interface OnboardingWizardProps {
  open: boolean;
  onComplete: () => void;
}

interface TourStepConfig {
  path: string;
  targetId: string;
  kicker: string;          // small caps label above the title (e.g. "STEP 1 / 4 — SETUP")
  title: string;           // serif headline
  description: string;     // single sentence, plain prose
}

// Tour copy is intentionally tight: one verb-led headline + one sentence.
// We never reference features that don't exist yet (no Email Campaigns).
const STEP_CONFIGS: TourStepConfig[] = [
  {
    path: '/settings/company',
    targetId: 'tour-company-info',
    kicker: 'Setup',
    title: 'Tell us about your company.',
    description:
      'Add your industry, services, and a short description. The AI uses this profile to write outreach in your voice and surface leads that fit you.',
  },
  {
    path: '/ai-crm/lead-generation',
    targetId: 'tour-lead-search',
    kicker: 'Discover',
    title: 'Find leads with one prompt.',
    description:
      'Type a query like "Marketing Director of FMCG companies in Hong Kong" — the AI returns matching companies with enriched contact details.',
  },
  {
    path: '/ai-crm/leads',
    targetId: 'tour-leads-filters',
    kicker: 'Organise',
    title: 'Your pipeline lives here.',
    description:
      'Every lead you discover lands in this list. Filter by status, priority, or owner, and move them through your sales workflow.',
  },
  {
    path: '/ai-crm/lead-generation',
    targetId: 'tour-lead-credits',
    kicker: 'Track',
    title: 'Watch your credits.',
    description:
      'Each contact reveal costs one credit. The chip in the top header always shows what you have left this period — click it for full usage details.',
  },
];

// Tokens shared with PricingCards. Keeping them inline here keeps the
// component self-contained and avoids a cross-module dependency.
const INK = '#0B1220';
const TEXT_MUTED = '#5B6478';
const ACCENT = '#3B5BFF';
const BORDER = '#E5E8EF';

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  open,
  onComplete,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Wait for a DOM element to appear, with retries — needed because we
  // navigate then mount the page on the next render.
  const waitForElement = useCallback((id: string, maxRetries = 25): Promise<HTMLElement | null> => {
    return new Promise((resolve) => {
      let retries = 0;
      const check = () => {
        const el = document.getElementById(id);
        if (el) {
          resolve(el);
        } else if (retries < maxRetries) {
          retries++;
          retryTimerRef.current = setTimeout(check, 150);
        } else {
          resolve(null);
        }
      };
      check();
    });
  }, []);

  const goToStep = useCallback(async (stepIndex: number) => {
    if (stepIndex < 0 || stepIndex >= STEP_CONFIGS.length) return;

    const config = STEP_CONFIGS[stepIndex];
    setTourOpen(false);

    if (location.pathname !== config.path) {
      navigate(config.path);
    }

    const el = await waitForElement(config.targetId);
    setTargetEl(el);
    setCurrentStep(stepIndex);
    setTimeout(() => setTourOpen(true), 100);
  }, [location.pathname, navigate, waitForElement]);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      goToStep(0);
    } else {
      setTourOpen(false);
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const stepConfig = STEP_CONFIGS[currentStep];
  const isLastStep = currentStep === STEP_CONFIGS.length - 1;
  const isFirstStep = currentStep === 0;

  // Editorial card body — serif headline, ink palette, sliding-pill progress dots.
  const renderBody = () => (
    <div
      style={{
        fontFamily: '"Inter", system-ui, sans-serif',
        padding: '4px 4px 0',
        color: INK,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: ACCENT,
          marginBottom: 10,
        }}
      >
        {stepConfig.kicker} · Step {currentStep + 1} / {STEP_CONFIGS.length}
      </div>

      <div
        style={{
          fontFamily: '"Fraunces", "Tiempos Headline", Georgia, serif',
          fontWeight: 600,
          fontSize: 22,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          color: INK,
          marginBottom: 12,
        }}
      >
        {stepConfig.title}
      </div>

      <p
        style={{
          margin: '0 0 20px 0',
          fontSize: 14,
          lineHeight: 1.55,
          color: TEXT_MUTED,
        }}
      >
        {stepConfig.description}
      </p>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {STEP_CONFIGS.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === currentStep ? 22 : 8,
              height: 4,
              borderRadius: 999,
              backgroundColor: i <= currentStep ? INK : BORDER,
              transition: 'all 220ms ease',
              display: 'inline-block',
            }}
          />
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setTourOpen(false);
            onComplete();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: TEXT_MUTED,
            fontSize: 13,
            fontWeight: 500,
            padding: 0,
            fontFamily: 'inherit',
          }}
        >
          Skip tour
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          {!isFirstStep && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToStep(currentStep - 1);
              }}
              style={{
                background: 'transparent',
                color: INK,
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'background-color 150ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F7F8FB')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (isLastStep) {
                setTourOpen(false);
                onComplete();
              } else {
                goToStep(currentStep + 1);
              }
            }}
            style={{
              background: INK,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 8,
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background-color 150ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1A2238')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = INK)}
          >
            {isLastStep ? "Let's go" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  const steps: TourProps['steps'] = [
    {
      // We render everything inside `description` so we control the typography
      // and layout entirely. The `title` field is left empty to avoid antd's
      // default heading style competing with our Fraunces title.
      title: null,
      description: renderBody(),
      target: () => targetEl!,
      placement: 'bottom',
      nextButtonProps: { style: { display: 'none' } },
      prevButtonProps: { style: { display: 'none' } },
    },
  ];

  if (!open || !stepConfig) return null;

  return (
    <>
      {/* Scoped style overrides so the antd Tour popover matches the
          editorial card. We don't touch the global antd theme. */}
      <style>{`
        .leadgenix-onboarding .ant-tour-inner {
          border-radius: 16px !important;
          box-shadow: 0 24px 48px rgba(11, 18, 32, 0.18), 0 2px 4px rgba(11, 18, 32, 0.06) !important;
          padding: 24px !important;
          width: 380px !important;
          max-width: calc(100vw - 32px) !important;
          background: #FFFFFF !important;
          border: 1px solid ${BORDER} !important;
        }
        .leadgenix-onboarding .ant-tour-arrow::before {
          background: #FFFFFF !important;
        }
        .leadgenix-onboarding .ant-tour-close { top: 14px !important; right: 14px !important; }
        .leadgenix-onboarding .ant-tour-close-x { color: ${TEXT_MUTED} !important; }
        .leadgenix-onboarding .ant-tour-content { padding-top: 4px !important; }
        .leadgenix-onboarding .ant-tour-footer { display: none !important; }
      `}</style>

      <Tour
        rootClassName="leadgenix-onboarding"
        open={tourOpen && !!targetEl}
        onClose={() => {
          setTourOpen(false);
          onComplete();
        }}
        steps={steps}
        mask={{
          style: { boxShadow: 'inset 0 0 0 9999px rgba(11, 18, 32, 0.55)' },
          color: 'rgba(11, 18, 32, 0.55)',
        }}
        type="primary"
        scrollIntoViewOptions
        animated
      />
    </>
  );
};

export default OnboardingWizard;
